import {
  ReactNode,
  Ref,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { BrandKitAssetPreview } from "../BrandKitAssetPreview";
import UploadFiles, { UploadFilesRef } from "components/uploader/UploadFiles/";
import { CREATE_ASSET_REQUEST_TYPE } from "../../hooks/useBrandkitEdit";
import { AssetSource, BrandKitAsset } from "pages/media/types/";
import { AssetPurpose } from "../BrandKitAssets";
import { Flex, message, Progress, UploadFile } from "antd";
import styled from "styled-components";
import { BaseRecord, useCustomMutation } from "@refinedev/core";
import { useAntTheme } from "hooks/useAntTheme";
import { useTranslate } from "#src/hooks";
import { useAnalytics } from "hooks/useAnalytics";
import { AnalyticsEvents } from "types/analytics";
import { StagedBrandKitAsset } from "types/";
import {
  getUploadFileBlob,
  putFileToS3,
  mergeStagedFilesForPurpose,
} from "./helpers";

export type AssetType = "Picture" | "Video" | "Music" | "Font";
export type AssetCategory = "AssetImage" | "Video" | "Music" | "Font";

export interface AssetUploaderConfig {
  assetType: AssetType;
  category: AssetCategory;
  acceptedMimeTypes: string;
  purposeValue: AssetPurpose;
}

export type AssetUploaderProps = {
  config: AssetUploaderConfig;
  assets: BrandKitAsset[];
  stagedAssets: StagedBrandKitAsset[];
  setStagedAssets: React.Dispatch<React.SetStateAction<StagedBrandKitAsset[]>>;
  onAssetDeleteSuccess?: (assetId: string) => void;
  organizationId: string;
  uploadRef?: Ref<UploadFilesRef | undefined>;
  hint?: ReactNode;
  showPreviewOverride?: boolean;
  /** Suppresses the in-progress/just-uploaded preview, independent of showPreviewOverride. */
  hideUploadFeedback?: boolean;
  /** Fires once the file has actually finished uploading (unlike UploadFiles' own onFilesAdded, which fires on selection). */
  onUploadComplete?: (files: UploadFile[]) => void;
  onUploadSuccess?: (asset: BrandKitAsset) => void;
  onUploadingChange?: (isUploading: boolean) => void;
  brandKitId: string;
  children?: ReactNode;
};

const AssetPreviewWrapper = styled.div`
  .ant-upload-list-item-container {
    margin: auto;
  }
`;

type PendingUpload = {
  file: UploadFile;
  percent: number;
};

type UploadedAsset = {
  asset: BrandKitAsset;
  uid: string;
};

export const AssetUploader = ({
  config,
  assets,
  stagedAssets,
  setStagedAssets,
  onAssetDeleteSuccess,
  organizationId,
  uploadRef,
  hint = <></>,
  showPreviewOverride = true,
  hideUploadFeedback = false,
  onUploadComplete,
  onUploadSuccess,
  onUploadingChange,
  brandKitId,
  children,
}: AssetUploaderProps) => {
  const { assetType, category, acceptedMimeTypes, purposeValue } = config;
  const existingAsset = assets.find((asset) => asset.purpose === purposeValue);
  const resolvedPurpose: AssetPurpose =
    assetType === "Music" ? "Music" : purposeValue;

  const t = useTranslate();
  const { theme } = useAntTheme();
  const { trackEvent } = useAnalytics();
  const { mutateAsync: createAsset } = useCustomMutation<BaseRecord>();

  const [pendingUpload, setPendingUpload] = useState<PendingUpload | null>(
    null
  );
  const [uploadedAsset, setUploadedAsset] = useState<UploadedAsset | null>(
    null
  );

  const blobUrlRef = useRef<string | null>(null);
  const internalUploadFilesRef = useRef<UploadFilesRef>(null);

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
    };
  }, []);

  // Expose our own handle on `uploadRef` instead of forwarding it straight to
  // the inner `<UploadFiles>` — so a caller resetting this slot from outside
  // (e.g. a sibling background upload superseding this one) also drops our
  // locally-cached "just uploaded" asset, not just the shared file list.
  useImperativeHandle(
    uploadRef,
    () => ({
      upload: (customUrl) =>
        internalUploadFilesRef.current?.upload(customUrl) ??
        Promise.resolve(undefined),
      reset: () => {
        internalUploadFilesRef.current?.reset();
        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
          blobUrlRef.current = null;
        }
        setUploadedAsset(null);
        setPendingUpload(null);
      },
      updateProgress: (uid, percent) => {
        internalUploadFilesRef.current?.updateProgress(uid, percent);
      },
    }),
    []
  );

  const uploadImmediately = async (file: UploadFile, files: UploadFile[]) => {
    const blob = getUploadFileBlob(file);
    if (!blob) return;

    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
    }
    const blobUrl = URL.createObjectURL(blob);
    blobUrlRef.current = blobUrl;

    setPendingUpload({ file, percent: 0 });
    onUploadingChange?.(true);

    try {
      const response = await createAsset({
        url: `media/${organizationId}/brand_kits/${brandKitId}/assets`,
        method: "post",
        values: {
          type: CREATE_ASSET_REQUEST_TYPE,
          organization_id: organizationId,
          category,
          source: AssetSource.UIGenerated,
          name: file.name,
          purpose: resolvedPurpose,
          asset: {
            type: assetType,
            file_name: file.name,
          },
        },
      });

      const assetId = response.data.asset_id;
      const target: string =
        response.data.target || response.data.target_signed_dict;

      if (!assetId || !target) {
        throw new Error("Missing asset_id/upload URL in create-asset response");
      }

      await putFileToS3(target, blob, (percent) => {
        setPendingUpload((prev) => (prev ? { ...prev, percent } : prev));
        internalUploadFilesRef.current?.updateProgress(file.uid, percent);
      });

      const asset = {
        asset_id: assetId,
        name: file.name,
        type: assetType,
        purpose: resolvedPurpose,
        path: blobUrl,
      } as BrandKitAsset;

      setUploadedAsset({ uid: file.uid, asset });

      trackEvent(AnalyticsEvents.BrandKitAssetUpload, {
        brandkit_id: brandKitId,
        asset_type: assetType,
      });

      onUploadSuccess?.(asset);
      onUploadComplete?.(files);
    } catch (error) {
      console.error("Error uploading brand kit asset", error);
      message.error(t("src.components.UploadFiles.uploadFailed"));
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      setStagedAssets((prev) => prev.filter((a) => a.uid !== file.uid));
    } finally {
      setPendingUpload(null);
      onUploadingChange?.(false);
      // Clear only the shared file list (not our own confirmed-asset state,
      // which we want to keep showing after our own successful upload).
      internalUploadFilesRef.current?.reset();
    }
  };

  const handleFilesAdded = (files: UploadFile[]) => {
    setStagedAssets((prev) =>
      mergeStagedFilesForPurpose(prev, files, purposeValue, assetType)
    );
    const file = files[0];
    if (file) {
      void uploadImmediately(file, files);
    }
  };

  const handleDeleteSuccess = (assetId: string) => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    if (uploadedAsset) {
      setStagedAssets((prev) =>
        prev.filter((a) => a.uid !== uploadedAsset.uid)
      );
    }
    setUploadedAsset(null);
    onAssetDeleteSuccess?.(assetId);
  };

  // Strip preview-only fields (blob: path, asset_id, etc.) before passing to
  // UploadFiles — useUploadFiles spreads matching staged entries into the
  // create-asset request body, so any extra field leaks to the backend.
  const uploadStagedItems = stagedAssets.map(({ uid, purpose }) => ({
    uid,
    ...(purpose && { purpose }),
  }));

  if (!hideUploadFeedback && pendingUpload) {
    const blob = getUploadFileBlob(pendingUpload.file);
    return (
      <Flex vertical gap={10} style={{ width: "fit-content" }}>
        <BrandKitAssetPreview
          isNew
          asset={{
            file: blob as File,
            name: pendingUpload.file.name,
            type: assetType,
            uid: pendingUpload.file.uid,
            purpose: resolvedPurpose,
          }}
          purposeValue={purposeValue}
          brandKitId={brandKitId}
        />
        <Progress
          strokeColor={theme.colorPrimary}
          percent={pendingUpload.percent}
        />
      </Flex>
    );
  }

  if (!hideUploadFeedback && uploadedAsset) {
    return (
      <BrandKitAssetPreview
        key={uploadedAsset.asset.asset_id}
        asset={uploadedAsset.asset}
        purposeValue={purposeValue}
        onDeleteSuccess={handleDeleteSuccess}
        brandKitId={brandKitId}
      />
    );
  }

  if (showPreviewOverride && existingAsset) {
    return (
      <BrandKitAssetPreview
        key={existingAsset.asset_id}
        asset={existingAsset}
        purposeValue={purposeValue}
        onDeleteSuccess={handleDeleteSuccess}
        brandKitId={brandKitId}
      />
    );
  }

  return (
    <AssetPreviewWrapper>
      <UploadFiles
        hint={hint}
        defaultItems={assets}
        stagedItems={uploadStagedItems}
        acceptedMimeTypes={acceptedMimeTypes}
        ref={internalUploadFilesRef}
        onFilesAdded={handleFilesAdded}
        maxCount={1}
        multiple={false}
        itemRequestProperties={{
          type: CREATE_ASSET_REQUEST_TYPE,
          organization_id: organizationId,
          category,
          source: AssetSource.UIGenerated,
          asset: {
            type: assetType,
          },
          ...(assetType === "Music" && { purpose: "Music" }),
          ...(assetType === "Font" && { purpose: purposeValue }),
          ...(assetType === "Picture" && { purpose: purposeValue }),
          ...(assetType === "Video" && { purpose: purposeValue }),
        }}
      >
        {children}
      </UploadFiles>
    </AssetPreviewWrapper>
  );
};

export const MIME_TYPES = {
  image: ".png, .jpeg, .jpg, image/jpeg, image/jpg, image/png",
  video: ".mp4, video/mp4, application/mp4, video/x-m4v, video/quicktime",
  music: ".mp3, audio/mp3, audio/mpeg, audio/mpeg3, audio/x-mpeg-3, audio/mp4",
  font: ".ttf, font/ttf, application/x-font-ttf, application/font-sfnt",
} as const;
